import { BffApiService } from '#services/bffApi.service';
import { useFiltersStore } from '#stores/filters.store';
import { GetFilterDefinitionViewEnum, type ProductTaxonomies, type StorePerimeters } from '@carrefour-gcs/bff-sdk';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useNotification } from '../notifications/useNotification';
import { useLanguage } from '../useLanguage';

/**
 * Composable used for fetching the data of FilterDefinition from BFF API
 */
export const useFilterDefinition = () => {
  const { t } = useLanguage();
  const route = useRoute();
  const filterStore = useFiltersStore();
  const notification = useNotification();

  const taxonomy = ref<ProductTaxonomies | null>(null);
  const storePerimeter = ref<StorePerimeters | null>(null);
  const view = ref<GetFilterDefinitionViewEnum | null>(null);

  watch(
    () => [filterStore.filters, route.meta.filterView] as const,
    async ([newFilters, filterView]) => {
      if (!newFilters.taxonomy || !newFilters.storePerimeter) {
        return;
      }

      taxonomy.value = newFilters.taxonomy as ProductTaxonomies;
      storePerimeter.value = newFilters.storePerimeter as StorePerimeters;
      view.value = filterView as GetFilterDefinitionViewEnum;
    },
    { deep: true, immediate: true }
  );

  const { data, isLoading, error, isError, isSuccess, isFetching } = useQuery({
    queryKey: computed(() => ['filterDefinitions', taxonomy.value, storePerimeter.value, view.value]), // Reactive query key based on taxonomy and storePerimeter
    queryFn: async () => {
      const {
        data: { data }
      } = await BffApiService.instance.FilterDefinition.getFilterDefinition({
        view: view.value!,
        taxonomy: taxonomy.value!,
        storePerimeter: storePerimeter.value!
      });

      return data;
    },
    enabled: () => !!taxonomy.value && !!storePerimeter.value
  });

  if (isError.value) {
    notification.notify({
      status: 'error',
      title: t('commonLabel.errorData'),
      body: t('Please try again later or contact the administrator.')
    });

    console.error('Error fetching filter definitions:', error.value);
  }

  return {
    data,
    isLoading,
    error,
    isSuccess,
    isFetching
  };
};
